import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ManageCompanyModal from "./index";

const ManageCompanyContext = createContext(null);

const URL_PARAM = "manageCompanyId";

export function ManageCompanyProvider({ children }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const urlValue = searchParams.get(URL_PARAM);
    const [isOpen, setIsOpen] = useState(Boolean(urlValue));
    const [companyId, setCompanyId] = useState(urlValue || null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (urlValue && urlValue !== companyId) {
            setCompanyId(urlValue);
            setIsOpen(true);
        }
    }, [urlValue, companyId]);

    const writeUrlParam = useCallback((value) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value) next.set(URL_PARAM, value);
            else next.delete(URL_PARAM);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const open = useCallback((id = null) => {
        setCompanyId(id || null);
        setIsOpen(true);
        if (id) writeUrlParam(String(id));
    }, [writeUrlParam]);

    const close = useCallback(() => {
        setIsOpen(false);
        setCompanyId(null);
        writeUrlParam(null);
    }, [writeUrlParam]);

    const selectCompany = useCallback((id) => {
        setCompanyId(id);
        if (id) writeUrlParam(String(id));
    }, [writeUrlParam]);

    const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

    const value = useMemo(
        () => ({ isOpen, companyId, open, close, selectCompany, refresh, refreshKey }),
        [isOpen, companyId, open, close, selectCompany, refresh, refreshKey]
    );

    return (
        <ManageCompanyContext.Provider value={value}>
            {children}
            <ManageCompanyModal />
        </ManageCompanyContext.Provider>
    );
}

export function useManageCompany() {
    const ctx = useContext(ManageCompanyContext);
    if (!ctx) throw new Error("useManageCompany must be used inside ManageCompanyProvider");
    return ctx;
}
